import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { IconEdit, IconHeadset, IconPlugConnected, IconPlus, IconTrash } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { useOrg } from '@/hooks/use-org';
import {
  AgentStatusDefinitionsSubscription,
  CreateAgentStatusDefinitionMutation,
  DeleteAgentStatusDefinitionMutation,
  OrganizationMembersSubscription,
  UpdateAgentStatusDefinitionMutation,
  UpdateMemberAgentConfigMutation,
  WebrtcCredentialsSubscription,
} from './-components/graphql';

const MANTINE_COLORS = [
  'gray',
  'red',
  'pink',
  'grape',
  'violet',
  'indigo',
  'blue',
  'cyan',
  'teal',
  'green',
  'lime',
  'yellow',
  'orange',
].map((color) => ({ value: color, label: `${color.charAt(0).toUpperCase()}${color.slice(1)}` }));

const SKILL_LEVELS = ['novice', 'intermediate', 'advanced', 'expert'] as const;

const SKILL_LEVEL_OPTIONS = SKILL_LEVELS.map((level) => ({
  value: level,
  label: `${level.charAt(0).toUpperCase()}${level.slice(1)}`,
}));

type SkillLevel = (typeof SKILL_LEVELS)[number];

type Skill = { name: string; level: SkillLevel };

type StatusForm = {
  name: string;
  label: string;
  color: string;
  availableForRouting: boolean;
  displayOrder: number;
};

const EMPTY_STATUS_FORM: StatusForm = {
  name: '',
  label: '',
  color: 'gray',
  availableForRouting: false,
  displayOrder: 0,
};

type AgentForm = {
  extensionNumber: string;
  skills: Skill[];
  maxConcurrentCalls: number;
  autoAnswer: boolean;
  wrapUpTimeSecs: number;
};

export const Route = createFileRoute('/_authed/settings/contact-center')({
  component: SettingsContactCenterPage,
});

function parseSkills(skills: unknown): Skill[] {
  if (!Array.isArray(skills)) return [];

  return skills
    .map((skill) => skill as Partial<Skill>)
    .filter(
      (skill): skill is Skill => typeof skill.name === 'string' && SKILL_LEVELS.includes(skill.level as SkillLevel),
    );
}

function StatusFormFields({
  form,
  isEditing,
  isSystem,
  onChange,
}: {
  form: StatusForm;
  isEditing: boolean;
  isSystem: boolean;
  onChange: (form: StatusForm) => void;
}) {
  return (
    <>
      <TextInput
        label="Label"
        placeholder="e.g., On Break"
        value={form.label}
        onChange={(event) =>
          onChange(
            isEditing
              ? { ...form, label: event.currentTarget.value }
              : {
                  ...form,
                  label: event.currentTarget.value,
                  name: event.currentTarget.value.toLowerCase().replace(/\s+/g, '_'),
                },
          )
        }
        required
        data-autofocus
      />
      {!isEditing && (
        <TextInput
          label="Internal Name"
          description="Auto-generated from label. Used as the status identifier."
          value={form.name}
          onChange={(event) => onChange({ ...form, name: event.currentTarget.value })}
          required
        />
      )}
      <Select
        label="Color"
        data={MANTINE_COLORS}
        value={form.color}
        onChange={(value) => value && onChange({ ...form, color: value })}
      />
      <Switch
        label="Available for routing"
        description="Agents in this status will receive calls from queues"
        checked={form.availableForRouting}
        onChange={(event) => onChange({ ...form, availableForRouting: event.currentTarget.checked })}
        disabled={isSystem}
      />
      <NumberInput
        label="Display Order"
        value={form.displayOrder}
        onChange={(value) => onChange({ ...form, displayOrder: Number(value) || 0 })}
        min={0}
      />
    </>
  );
}

function AgentConfigModal({
  member,
  onClose,
}: {
  member: { id: string; email: string; config: AgentForm } | null;
  onClose: () => void;
}) {
  const [, updateAgentConfig] = useMutation(UpdateMemberAgentConfigMutation);
  const [form, setForm] = useState<AgentForm>(member?.config ?? { ...EMPTY_AGENT_FORM });
  const [newSkillName, setNewSkillName] = useState('');
  const [newSkillLevel, setNewSkillLevel] = useState<SkillLevel>('intermediate');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!member) return null;

  const save = async () => {
    setIsSaving(true);
    setError(null);

    const result = await updateAgentConfig({
      id: member.id,
      changes: {
        extensionNumber: form.extensionNumber.trim() || null,
        skills: form.skills,
        maxConcurrentCalls: form.maxConcurrentCalls,
        autoAnswer: form.autoAnswer,
        wrapUpTimeSecs: form.wrapUpTimeSecs,
      },
    });

    setIsSaving(false);

    if (result.error) return setError(getErrorMessage(result.error, 'Failed to save the agent configuration'));

    onClose();
  };

  return (
    <Modal opened onClose={onClose} title={`Agent Configuration — ${member.email}`} size="lg">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <Stack gap="md">
          <ErrorAlert message={error} />
          <TextInput
            label="Extension Number"
            placeholder="e.g., 101"
            value={form.extensionNumber}
            onChange={(event) => setForm((f) => ({ ...f, extensionNumber: event.currentTarget.value }))}
            data-autofocus
          />
          <Group grow>
            <NumberInput
              label="Max Concurrent Calls"
              value={form.maxConcurrentCalls}
              onChange={(value) => setForm((f) => ({ ...f, maxConcurrentCalls: Number(value) || 1 }))}
              min={1}
              max={10}
            />
            <NumberInput
              label="Wrap-Up Time (seconds)"
              value={form.wrapUpTimeSecs}
              onChange={(value) => setForm((f) => ({ ...f, wrapUpTimeSecs: Number(value) || 0 }))}
              min={0}
            />
          </Group>
          <Switch
            label="Auto-Answer"
            description="Automatically answer incoming queue calls"
            checked={form.autoAnswer}
            onChange={(event) => setForm((f) => ({ ...f, autoAnswer: event.currentTarget.checked }))}
          />

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Skills
            </Text>
            {form.skills.map((skill, index) => (
              <Group key={`${skill.name}-${index}`} gap="xs">
                <Badge variant="light" color="indigo">
                  {skill.name}
                </Badge>
                <Badge variant="outline" color="gray" size="sm">
                  {skill.level}
                </Badge>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="xs"
                  onClick={() => setForm((f) => ({ ...f, skills: f.skills.filter((_, i) => i !== index) }))}
                >
                  <IconTrash size={12} />
                </ActionIcon>
              </Group>
            ))}
            <Group gap="xs">
              <TextInput
                placeholder="Skill name"
                value={newSkillName}
                onChange={(event) => setNewSkillName(event.currentTarget.value)}
                style={{ flex: 1 }}
                size="xs"
              />
              <Select
                data={SKILL_LEVEL_OPTIONS}
                value={newSkillLevel}
                onChange={(value) => value && setNewSkillLevel(value as SkillLevel)}
                size="xs"
                w={140}
              />
              <Button
                variant="light"
                size="xs"
                disabled={!newSkillName.trim()}
                onClick={() => {
                  setForm((f) => ({
                    ...f,
                    skills: [...f.skills, { name: newSkillName.trim(), level: newSkillLevel }],
                  }));
                  setNewSkillName('');
                }}
              >
                Add
              </Button>
            </Group>
          </Stack>

          <Group justify="flex-end" mt="xs">
            <Button variant="subtle" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSaving}>
              Save Agent Config
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

const EMPTY_AGENT_FORM: AgentForm = {
  extensionNumber: '',
  skills: [],
  maxConcurrentCalls: 1,
  autoAnswer: false,
  wrapUpTimeSecs: 30,
};

function WebrtcPanel({ organizationId }: { organizationId: string }) {
  const [{ data }] = useSubscription({ query: WebrtcCredentialsSubscription, variables: { organizationId } });
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const credentials = data?.webrtcCredentials[0] ?? null;

  const provision = async () => {
    setIsProvisioning(true);
    setError(null);

    const { error: apiError } = await api.organizations({ organizationId }).webrtc.provision.post();

    setIsProvisioning(false);

    if (apiError) setError(getErrorMessage(apiError, 'Failed to provision WebRTC'));
  };

  return (
    <Paper p="md" radius="lg" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <div>
            <Text fw={600}>Softphone Connectivity</Text>
            <Text size="sm" c="dimmed">
              Agents need a provisioned WebRTC connection before they can take calls in the browser.
            </Text>
          </div>
          {credentials ? (
            <Badge variant="light" color="green" size="lg">
              Provisioned
            </Badge>
          ) : (
            <Button
              variant="light"
              leftSection={<IconPlugConnected size={16} />}
              loading={isProvisioning}
              onClick={() => void provision()}
            >
              Provision WebRTC
            </Button>
          )}
        </Group>

        <ErrorAlert message={error} />

        {credentials && (
          <Group gap="xl">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Connection
              </Text>
              <Text size="sm" ff="monospace">
                {credentials.telnyxConnectionId}
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Webhook
              </Text>
              <Text size="sm" ff="monospace">
                {credentials.webhookUrl}
              </Text>
            </div>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}

function StatusDefinitionsPanel({ organizationId }: { organizationId: string }) {
  const [{ data }] = useSubscription({ query: AgentStatusDefinitionsSubscription, variables: { organizationId } });
  const [, createStatus] = useMutation(CreateAgentStatusDefinitionMutation);
  const [, updateStatus] = useMutation(UpdateAgentStatusDefinitionMutation);
  const [, deleteStatus] = useMutation(DeleteAgentStatusDefinitionMutation);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingIsSystem, setEditingIsSystem] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState<StatusForm>({ ...EMPTY_STATUS_FORM });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!data) {
    return (
      <Center h="30vh">
        <Loader size="lg" />
      </Center>
    );
  }

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setEditingIsSystem(false);
    setForm({ ...EMPTY_STATUS_FORM });
  };

  const openCreate = () => {
    setEditingId(null);
    setEditingIsSystem(false);
    setForm({
      ...EMPTY_STATUS_FORM,
      displayOrder: Math.max(-1, ...data.agentStatusDefinitions.map((s) => s.displayOrder)) + 1,
    });
    setModalOpen(true);
  };

  const openEdit = (definition: (typeof data.agentStatusDefinitions)[number]) => {
    setEditingId(definition.id);
    setEditingIsSystem(definition.isSystem);
    setForm({
      name: definition.name,
      label: definition.label,
      color: definition.color,
      availableForRouting: definition.availableForRouting,
      displayOrder: definition.displayOrder,
    });
    setModalOpen(true);
  };

  const save = async () => {
    setIsSaving(true);
    setError(null);

    const result = editingId
      ? await updateStatus({
          id: editingId,
          changes: {
            label: form.label,
            color: form.color,
            displayOrder: form.displayOrder,
            ...(editingIsSystem ? {} : { availableForRouting: form.availableForRouting }),
          },
        })
      : await createStatus({
          organizationId,
          name: form.name.toLowerCase().replace(/\s+/g, '_'),
          label: form.label,
          color: form.color,
          availableForRouting: form.availableForRouting,
          displayOrder: form.displayOrder,
        });

    setIsSaving(false);

    if (result.error) return setError(getErrorMessage(result.error, 'Failed to save the status'));

    closeModal();
  };

  const remove = async (id: string) => {
    setError(null);

    const result = await deleteStatus({ id });

    setConfirmDeleteId(null);

    if (result.error) setError(getErrorMessage(result.error, 'Failed to delete the status'));
  };

  return (
    <Paper p="md" radius="lg" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <div>
            <Text fw={600}>Agent Statuses</Text>
            <Text size="sm" c="dimmed">
              Define the statuses agents can set. Each status controls whether the agent receives queue calls.
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} variant="light" onClick={openCreate}>
            Add Status
          </Button>
        </Group>

        <ErrorAlert message={error} />

        {data.agentStatusDefinitions.length === 0 ? (
          <Stack align="center" gap="md" py="xl">
            <ThemeIcon size={48} radius="xl" variant="light" color="gray">
              <IconHeadset size={24} />
            </ThemeIcon>
            <Text size="sm" c="dimmed">
              No status definitions yet. Status definitions will be seeded when the contact center feature is activated.
            </Text>
          </Stack>
        ) : (
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Order</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Routable</Table.Th>
                <Table.Th>System</Table.Th>
                <Table.Th w={80} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.agentStatusDefinitions.map((definition) => (
                <Table.Tr key={definition.id}>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {definition.displayOrder}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={definition.color}>
                      {definition.label}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed" ff="monospace">
                      {definition.name}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="dot" color={definition.availableForRouting ? 'green' : 'gray'}>
                      {definition.availableForRouting ? 'Yes' : 'No'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {definition.isSystem && (
                      <Badge variant="outline" color="gray" size="xs">
                        System
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <ActionIcon variant="subtle" onClick={() => openEdit(definition)}>
                        <IconEdit size={14} />
                      </ActionIcon>
                      {!definition.isSystem &&
                        (confirmDeleteId === definition.id ? (
                          <Group gap={4}>
                            <Button size="compact-xs" variant="subtle" onClick={() => setConfirmDeleteId(null)}>
                              No
                            </Button>
                            <Button size="compact-xs" color="red" onClick={() => void remove(definition.id)}>
                              Yes
                            </Button>
                          </Group>
                        ) : (
                          <ActionIcon variant="subtle" color="red" onClick={() => setConfirmDeleteId(definition.id)}>
                            <IconTrash size={14} />
                          </ActionIcon>
                        ))}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      <Modal opened={modalOpen} onClose={closeModal} title={editingId ? 'Edit Status' : 'Add Status'}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <Stack gap="md">
            <StatusFormFields form={form} isEditing={!!editingId} isSystem={editingIsSystem} onChange={setForm} />
            <Group justify="flex-end" mt="xs">
              <Button variant="subtle" type="button" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={isSaving}
                disabled={!form.label.trim() || (!editingId && !form.name.trim())}
              >
                {editingId ? 'Save Changes' : 'Add Status'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Paper>
  );
}

function AgentConfigPanel({ organizationId }: { organizationId: string }) {
  const [{ data }] = useSubscription({ query: OrganizationMembersSubscription, variables: { organizationId } });
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);

  const members = (data?.organizationMembers ?? []).filter((member) => member.role !== 'owner');
  const editing = members.find((member) => member.id === editingMemberId) ?? null;

  return (
    <Paper p="md" radius="lg" withBorder>
      <Stack gap="md">
        <div>
          <Text fw={600}>Agent Configuration</Text>
          <Text size="sm" c="dimmed">
            Extensions, skills, and call handling defaults per agent. Owners are managed outside the contact center.
          </Text>
        </div>

        {members.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            No agents to configure yet.
          </Text>
        ) : (
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Agent</Table.Th>
                <Table.Th>Extension</Table.Th>
                <Table.Th>Skills</Table.Th>
                <Table.Th>Auto-Answer</Table.Th>
                <Table.Th>Wrap-Up</Table.Th>
                <Table.Th w={48} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {members.map((member) => (
                <Table.Tr key={member.id}>
                  <Table.Td>
                    <Text size="sm">{member.email}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace" c="dimmed">
                      {member.extensionNumber ?? '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {parseSkills(member.skills).map((skill) => (
                        <Badge key={skill.name} size="xs" variant="light" color="indigo">
                          {skill.name}
                        </Badge>
                      ))}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" variant="dot" color={member.autoAnswer ? 'green' : 'gray'}>
                      {member.autoAnswer ? 'On' : 'Off'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {member.wrapUpTimeSecs ?? 30}s
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon variant="subtle" color="indigo" onClick={() => setEditingMemberId(member.id)}>
                      <IconHeadset size={14} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      <AgentConfigModal
        key={editing?.id ?? 'none'}
        member={
          editing
            ? {
                id: editing.id,
                email: editing.email,
                config: {
                  extensionNumber: editing.extensionNumber ?? '',
                  skills: parseSkills(editing.skills),
                  maxConcurrentCalls: editing.maxConcurrentCalls ?? 1,
                  autoAnswer: editing.autoAnswer ?? false,
                  wrapUpTimeSecs: editing.wrapUpTimeSecs ?? 30,
                },
              }
            : null
        }
        onClose={() => setEditingMemberId(null)}
      />
    </Paper>
  );
}

function SettingsContactCenterPage() {
  const { organizationId } = useOrg();

  if (!organizationId) {
    return (
      <Center h="30vh">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack gap="lg">
      <div>
        <Title order={2}>Contact Center</Title>
        <Text size="sm" c="dimmed" mt="2xs">
          Configure agent status definitions and contact center settings.
        </Text>
      </div>

      <WebrtcPanel organizationId={organizationId} />
      <StatusDefinitionsPanel organizationId={organizationId} />
      <AgentConfigPanel organizationId={organizationId} />
    </Stack>
  );
}
